import {TTemplatePreviewDto} from '@shared/components/issues/types/template-preview-dto.type';
import { Injectable, inject } from "@angular/core";
import {Actions} from "@ngrx/effects";
import {select, Store} from "@ngrx/store";
import {exhaustMap, finalize, mergeMap, Observable, withLatestFrom} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {
  FileDto,
  FilterDto,
  FilterKind,
  FilterOperators,
  FilterTypes,
  IssueContentType,
  IssueListDto,
  IssueTemplateListDto,
  IssueType
} from "@server-models";
import {Router} from "@angular/router";
import {SharedImageService} from "@shared/services/shared-image.service";
import {SharedCameraService} from "@shared/services/shared-camera.service";
import {SharedModalLoaderService} from "@shared/services/shared-modal-loader.service";
import {TranslateService} from "@ngx-translate/core";
import {LinkIssueActions} from "@link/pages/issues/store/link-issue.actions-type";

import {SharedIssueBaseService} from "@shared/components/issues/services/shared-issue-base.service";
import {SharedIssueApiBaseEffects} from "@shared/components/issues/store/shared-issue-api-base.effects";
import {LinkLoginService} from "@link/pages/login/services/link-login.service";
import {SharedIssueBaseActions} from "@shared/components/issues/store/shared-issue-base.action-type";
import {TIssuePreview} from "@shared/components/issues/store/shared-issue-base.actions";
import {LinkIssueService} from "@link/pages/issues/services/link-issue.service";
import {SharedIssueService} from "@shared/components/issues/services/shared-issue.service";
import {LinkIssuesSelectors} from "@link/pages/issues/store/link-issues.selector-type";
import {FormGroup} from "@angular/forms";
import {IIssueService} from "@shared/components/issues/interfaces/issue-service.interface";
import {TSubmitIssueDto} from "@shared/components/issues/types/submit-issue-dto.type";

import {SharedCacheService} from "@shared/services/cache/shared-cache.service";
import {SharedFileApiService} from "@shared/services/file/shared-file-api.service";
import {ILocalIssueDetailData} from "@shared/components/issues/interfaces/local-issue-detail-data.interface";
import {SharedIssueApiService} from "@shared/components/issues/services/shared-issue-api.service";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";
import {TechIssuesActions} from "@tech/pages/issues/store/tech-issue.actions-type";
import {SharedLoginSelectors} from "@shared/components/login/store/shared-login.selector-type";

@Injectable({
  providedIn: 'root'
})
export class LinkIssuesApiEffects extends SharedIssueApiBaseEffects {
  private _linkIssuesService = inject(LinkIssueService);
  private _sharedFileApiService = inject(SharedFileApiService);
  private _linkLoginService = inject(LinkLoginService);


  constructor() {
    const actions$ = inject(Actions);
    const store = inject(Store);
    const _cameraService = inject(SharedCameraService);
    const _sharedModalLoaderService = inject(SharedModalLoaderService);
    const _translateService = inject(TranslateService);
    const _imageService = inject(SharedImageService);
    const _issuesBaseService = inject(SharedIssueBaseService);
    const _sharedIssueApiService = inject(SharedIssueApiService);
    const _issueService = inject(SharedIssueService);
    const _cacheService = inject(SharedCacheService);
    const _router = inject(Router);

    super(
      actions$,
      store,
      _cameraService,
      _sharedModalLoaderService,
      _translateService,
      _imageService,
      _issuesBaseService,
      _sharedIssueApiService,
      _issueService,
      _cacheService,
      _router
    )
  }

  override requestGetPaginated(action: ISharedLocalBodyRequest): Observable<(ILocalPageOutputDto<IssueListDto[]> | any)> {
    return this._sharedIssueApiService.getIssueRecentList(action.pagination, action.filters).pipe(
      map((data: ILocalPageOutputDto<IssueListDto[]>) =>
        action.isRefresh
          ? LinkIssueActions.getIssueListPaginatedRefresh({ data })
          : LinkIssueActions.getIssueListPaginatedSuccess({ data })
      ),
      catchError((error) => [LinkIssueActions.getIssueListPaginatedFail({ error })])
    )
  }

  override requestGetTemplate(templateId?: number, resourceId?: number, filters: FilterDto[] = [], pageNumber?: number): Observable<(ILocalPageOutputDto<IssueTemplateListDto[]> | any)> {
    filters = [
      ...filters,
      {
        kind: FilterKind.Default,
        type: FilterTypes.DataTransferObject,
        value: IssueContentType.DirectMessage.toString(),
        operator: FilterOperators.NotEqual,
        property: 'contentType'
      }
    ]
    return this._sharedIssueApiService.getIssueTemplateList(filters, pageNumber).pipe(
      map((data: ILocalPageOutputDto<IssueTemplateListDto[]>) =>
        LinkIssueActions.getIssueTemplateListSuccess({ data })
      ),
      catchError((error) => [LinkIssueActions.getIssueTemplateListFail({ error })])
    )
  }

  override formatReadDetailDataFinish(action$: Observable<{
    detailDataFormatted: ILocalIssueDetailData
  }>): Observable<any> {
    return action$;
  }

  override requestIssuePreviewByContentType(action: TIssuePreview): Observable<(unknown | any)> {
    const apiService: IIssueService = this._linkIssuesService.getApiServiceByIssueContentType(action.issueType);
    const preview: Observable<TTemplatePreviewDto> = action.issueType === IssueContentType.Connected
      ? this._linkIssuesService.previewConnected(action.templateId, action.resourceId)
      : apiService.getIssuePreview(action.templateId);
    return preview.pipe(
      map((data) =>
        LinkIssueActions.getIssuePreviewSuccess({ data: data, templateId: action.templateId, resourceId: action.resourceId, contentType: action.issueType })
      ),
      catchError((error) => [TechIssuesActions.getIssuePreviewFail({ error })])
    )
  }


  override  requestByIdByIssueType(action: { issueId: number, issueType: IssueType }): Observable<any> {
    const service: IIssueService = this._linkIssuesService.getApiServiceByIssueType(action.issueType);
    return service.getIssue(action.issueId).pipe(
      map((data) =>
        LinkIssueActions.getByIdSuccess({ data, cacheGuid: this._cacheService.generateGuid() })
      ),
      catchError((error) => [LinkIssueActions.getByIdFail({ error })])
    )
  }

  override prepareFormsToSendTemplatePreview(action$: Observable<{
    form: FormGroup,
    templatePreviewDto: any
  }>): Observable<any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe((select(LinkIssuesSelectors.selectCurrentPreviewIssueType)))),
      withLatestFrom(this.store.pipe(select(SharedLoginSelectors.selectLinkResource))),
      withLatestFrom(this.store.pipe(select(SharedLoginSelectors.selectTenant))),

      mergeMap(([[[{ form, templatePreviewDto }, templateIssueType], resourceDto], tenant]) => {
        const service = this._issueService.getIssueFormService(templateIssueType);
        const submitReady = service.prepareFormIssueDto(form, templatePreviewDto, resourceDto);
        return [LinkIssueActions.prepareFormsToSendTemplatePreviewDone({ submitReady })]
      })
    );
  }

  override sendTemplatePreview(action$: Observable<{
    issuePrepared: TSubmitIssueDto
  }>): Observable<void> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(LinkIssuesSelectors.selectCurrentPreviewId))),
      withLatestFrom(this.store.pipe((select(LinkIssuesSelectors.selectCurrentPreviewIssueType)))),
      mergeMap(([[{ issuePrepared }, templateId], issueContentType]) => {
        return this.sendTemplatePreviewByIssueType({
          templateId,
          issueContentType,
          issuePrepared,
        });
      })
    );
  }

  override sendTemplatePreviewByIssueType(action: {
    templateId: number,
    issuePrepared: TSubmitIssueDto
    issueContentType: IssueContentType
  }): Observable<(unknown | any)> {
    const apiService = this._linkIssuesService.getApiServiceByIssueContentType(action.issueContentType);
    return apiService.sendIssueTemplatePreviewById(action.templateId, action.issuePrepared).pipe(
      map(() => LinkIssueActions.sendTemplatePreviewSuccess({})),
      catchError(error => [LinkIssueActions.sendTemplatePreviewFail({ error })]),
      finalize(() => {
        this._sharedModalLoaderService.dismiss();
      })
    );
  }

  override saveSignatureRequest(tenantId: number, blob: Blob, dataUrl: string, fileType: string,
                                index: number | string): Observable<{
    signature: FileDto
  } | any> {
    return this._sharedFileApiService.upload(tenantId, blob, fileType).pipe(
      exhaustMap((data: FileDto) => {
          return [LinkIssueActions.saveSignatureSuccess({
            id: data.fileId!,
            dataUrl,
            index
          })]
        }
      ),
      catchError((error) => [LinkIssueActions.saveSignatureFail({ error })])
    )
  }

  override savePictureRequest(tenantId: number, blob: Blob, fileType: string, pictureId: number): Observable<{
    image: FileDto
  } | any> {
    return this._sharedFileApiService.upload(tenantId, blob, fileType).pipe(
      exhaustMap((data: FileDto) => {
          return [LinkIssueActions.savePictureSuccess({ fileId: data.fileId!, path: data.path!, pictureId })]
        }
      ),
      catchError((error) => [LinkIssueActions.savePictureFail({ error })])
    )
  }

}
